import React, { useEffect, useState } from 'react';
import { TeamOutlined, TrophyOutlined, UndoOutlined } from '@ant-design/icons';
import { App, Card, Slider, Space, Spin, Typography, theme, Button } from 'antd';
import { generate } from '@ant-design/colors';
import { _T } from '../../../../../base-react/src/Localization';
import { apis, models } from "@vote4dance/judging-sdk";
import { RoundPriorityType } from '@vote4dance/judging-types';
import { RoundPriorityTable } from './RoundPrioritySettings';
var Api = apis.Api;
var RoundItem = models.rounds.RoundItem;
var ClassRoundSource = models.rounds.ClassRoundSource;
import { SendTeamsThrough } from './SendTeamsThrough';
import { hasInvalidRoundPriorityGroups, sanitizeRoundPriorityGroups } from '../utilities';
const { Title } = Typography;
const { useToken } = theme;
export const PrioritizeTeams = (props) => {
    const { round, classRounds, disabled, previewRound } = props;
    const [groups, setGroups] = useState([]);
    const [teams, setTeams] = useState([]);
    const [type, setType] = useState('groups');
    const [isLoading, setIsLoading] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const { notification } = App.useApp();
    useEffect(() => {
        resetSettings();
    }, [round]);
    useEffect(() => {
        loadTeams();
    }, [round.round_id]);
    const loadTeams = async () => {
        setIsLoading(true);
        try {
            const result = await Api.call(`${ClassRoundSource(round.class_id).url}/${round.round_id}`, "prioritize-teams", { preview: true });
            setTeams(result);
            setIsLoading(false);
        }
        catch (err) {
            setIsLoading(false);
            notification.error({ message: err.message });
        }
    };
    const resetSettings = () => {
        const settings = RoundItem.getPrioritySettings(round);
        if (settings.type)
            setType(settings.type);
        if (settings.groups) {
            const sanitizedGroups = sanitizeRoundPriorityGroups(settings.groups, { preferMax: true });
            setGroups(sanitizedGroups.map((group) => ({ ...group, key: Math.random().toString() })));
        }
    };
    const onGroupChange = async (updatedGroups) => {
        const newList = sanitizeRoundPriorityGroups(updatedGroups, { preferMax: true });
        const settingsObject = {
            type,
            groups: newList
        };
        if (hasInvalidRoundPriorityGroups(newList)) {
            notification.error({ message: _T('round.details.round_priority_settings.invalid') });
            return;
        }
        setGroups(newList.map((group) => ({ ...group, key: Math.random().toString() })));
        await Api.update(`${ClassRoundSource(round.class_id).url}/${round.round_id}`, { ...round, round_priority_settings: JSON.stringify(settingsObject) });
    };
    const assignPriority = async () => {
        setConfirmLoading(true);
        try {
            await Api.call(`${ClassRoundSource(round.class_id).url}/${round.round_id}`, "prioritize-teams", {});
            notification.success({ message: _T('round.details.priority.updateSuccess') });
        }
        catch (err) {
            notification.error({ message: err.message });
        }
        setConfirmLoading(false);
    };
    const resetPriority = async () => {
        setConfirmLoading(true);
        try {
            await Api.call(`${ClassRoundSource(round.class_id).url}/${round.round_id}`, "reset-team-priorities", {});
        }
        catch (err) {
            notification.error({ message: err.message });
        }
        setConfirmLoading(false);
    };
    if (type === RoundPriorityType.Alternates) {
        return React.createElement(SendTeamsThrough, { round: round, classRounds: classRounds, alternates: true, disabled: disabled, previewRound: previewRound });
    }
    return (React.createElement(Card, { title: React.createElement(React.Fragment, null,
            React.createElement(TrophyOutlined, null),
            " ",
            _T('round.details.priority.update')) }, isLoading ? (React.createElement(Spin, null)) : (React.createElement(Space, { orientation: 'vertical', style: { width: '100%' } },
        React.createElement(RoundPriorityTable, { item: round, groups: groups, disabled: disabled || false, allowChange: false, setGroups: onGroupChange }),
        React.createElement(PrioritizeGroupsPreview, { groups: groups, teams: teams, onChange: onGroupChange, disabled: disabled }),
        React.createElement(Space, null,
            React.createElement(Button, { loading: confirmLoading, icon: React.createElement(TeamOutlined, null), onClick: assignPriority, disabled: disabled }, _T('round.details.priority.update')),
            React.createElement(Button, { loading: confirmLoading, icon: React.createElement(UndoOutlined, null), type: "dashed", onClick: resetPriority, disabled: disabled }, _T('round.details.priority.reset')))))));
};
const PrioritizeGroupsPreview = (props) => {
    var _a;
    const { groups, teams, onChange, disabled } = props;
    const { token } = useToken();
    const calculateAccumulatedPlacements = (teams) => {
        if (!teams || !Array.isArray(teams)) {
            console.error("Teams array is invalid:", teams);
            return [];
        }
        const placementCounts = teams.reduce((acc, team) => {
            const placement = team.heats_team_placement || 0;
            acc[placement] = (acc[placement] || 0) + 1;
            return acc;
        }, { 0: 0 });
        let cumulativeCount = 0;
        return Object.entries(placementCounts)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([placement, count]) => {
            cumulativeCount += count;
            return {
                placement: Number(placement),
                count: cumulativeCount,
            };
        });
    };
    const uniqueCounts = calculateAccumulatedPlacements(teams);
    const minCount = (_a = uniqueCounts[0]) === null || _a === void 0 ? void 0 : _a.count;
    const maxCount = teams && teams.length;
    const primaryColorShades = generate(token.colorPrimary, { theme: 'default' });
    const groupColors = groups && groups.map((_, index) => {
        const shadeIndex = Math.floor((index / groups.length) * primaryColorShades.length);
        return primaryColorShades[shadeIndex];
    });
    const calculateInitialSliderValues = () => {
        let cumulativeCount = 0;
        const sliderValues = [0]; // Start with 0 as the first slider value
        sliderValues.push(...[...groups].map((group) => {
            if (group.max) {
                cumulativeCount += group.max;
                return cumulativeCount;
            }
            return maxCount;
        }));
        return sliderValues;
    };
    const [sliderValues, setSliderValues] = useState(calculateInitialSliderValues());
    useEffect(() => {
        const newSliderValues = calculateInitialSliderValues();
        setSliderValues(newSliderValues);
    }, [groups]);
    const calculateGroupMax = (values) => {
        const result = [];
        let previousCount = 0;
        let startPlacement = uniqueCounts && uniqueCounts[0] ? uniqueCounts[0].placement : 0;
        for (let i = 1; i < values.length; i++) {
            const targetCount = values[i];
            if (targetCount === undefined) {
                console.error(`Target count is undefined at index ${i}`);
                continue;
            }
            const matchingEntry = uniqueCounts.find((entry) => entry.count >= targetCount);
            if (!matchingEntry) {
                console.error(`No matching entry found in uniqueCounts for target count: ${targetCount}`);
                continue;
            }
            const endPlacement = matchingEntry.placement;
            const groupCount = targetCount - previousCount;
            result.push({
                ...groups[i - 1],
                max: groupCount,
                startPlacement,
                endPlacement,
                count: groupCount,
            });
            previousCount = targetCount;
            const nextEntryIndex = uniqueCounts.indexOf(matchingEntry);
            const nextEntry = uniqueCounts[nextEntryIndex + 1];
            if (nextEntry) {
                startPlacement = nextEntry.placement;
            }
            else {
                startPlacement = matchingEntry.placement;
            }
        }
        return result;
    };
    const handleSliderChange = (values, complete) => {
        const firstValue = values[0];
        if (firstValue !== 0) {
            values[1] = firstValue;
            values[0] = 0;
        }
        setSliderValues(values);
        const updatedGroups = calculateGroupMax(values);
        if (complete && onChange) {
            onChange(updatedGroups);
        }
    };
    const marks = uniqueCounts.reduce((acc, { placement, count }) => {
        acc[count] = count.toString();
        return acc;
    }, {});
    return (React.createElement(Space, { orientation: "vertical", style: { width: '100%' } },
        React.createElement(Slider, { range: true, marks: marks, step: null, min: minCount, max: maxCount, value: sliderValues, disabled: disabled, onChange: (values) => handleSliderChange(values, false), onChangeComplete: (values) => handleSliderChange(values, true), tooltip: {
                open: true
            }, trackStyle: sliderValues.map((_, index) => ({
                backgroundColor: groupColors[index],
            })), handleStyle: sliderValues.map((_, index) => ({
                backgroundColor: groupColors[index],
                borderColor: groupColors[index],
            })) }),
        React.createElement(Space, { orientation: "vertical", style: { width: '100%' } }, calculateGroupMax(sliderValues).map((group, index) => {
            return (React.createElement("div", { key: index, style: {
                    padding: '8px',
                    border: `1px solid ${groupColors[index]}`,
                    borderRadius: '4px',
                    backgroundColor: `${groupColors[index]}20`,
                } },
                React.createElement(Title, { level: 5, style: { margin: 0 } },
                    group.name,
                    ": ",
                    _T('team.details.team_placement.label'),
                    " ",
                    group.startPlacement,
                    " - ",
                    group.endPlacement,
                    " (",
                    group.count,
                    ")")));
        }))));
};
